import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, MessageCircle, Sparkles } from 'lucide-react';
import apiClient from '../../utils/api';
import './DailyCheckinDialog.css';

function DailyCheckinDialog({ onClose, onStartChat }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isGerman = i18n.language === 'de';
  const [checkinData, setCheckinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMood, setSelectedMood] = useState(null);

  const moods = [
    { value: 1, emoji: '😔', label: isGerman ? 'Nicht gut' : 'Not good' },
    { value: 2, emoji: '😐', label: isGerman ? 'Geht so' : 'So-so' },
    { value: 3, emoji: '🙂', label: isGerman ? 'Okay' : 'Okay' },
    { value: 4, emoji: '😊', label: isGerman ? 'Gut' : 'Good' },
    { value: 5, emoji: '😄', label: isGerman ? 'Super' : 'Great' },
  ];

  useEffect(() => {
    loadCheckinData();
  }, []);

  const loadCheckinData = async () => {
    try {
      const data = await apiClient.getDailyCheckinData();
      setCheckinData(data);
    } catch (error) {
      console.error('Error loading checkin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMoodSelect = async (mood) => {
    setSelectedMood(mood);
  };

  const handleStartConversation = async () => {
    try {
      // Log the daily checkin
      await apiClient.logDailyCheckin(selectedMood);
      
      // Navigate to chat with context
      onClose();
      navigate('/chat', { 
        state: { 
          initialMood: selectedMood,
          fromDailyCheckin: true 
        } 
      });
    } catch (error) {
      console.error('Error logging checkin:', error);
      onClose();
      navigate('/chat');
    }
  };

  const handleSkip = async () => {
    try {
      await apiClient.skipDailyCheckin();
    } catch (error) {
      console.error('Error skipping checkin:', error);
    }
    onClose();
  };

  const getPersonalizedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return isGerman ? 'Guten Morgen' : 'Good morning';
    } else if (hour < 18) {
      return isGerman ? 'Guten Tag' : 'Good afternoon';
    } else {
      return isGerman ? 'Guten Abend' : 'Good evening';
    }
  };

  const getInsightMessage = () => {
    if (!checkinData) return null;

    const { recent_frequencies, journal_entries, last_mood, streak_days } = checkinData;

    // Build personalized message based on user data
    if (streak_days > 3) {
      return isGerman 
        ? `Du bist jetzt ${streak_days} Tage in Folge aktiv! 🔥`
        : `You've been active for ${streak_days} days in a row! 🔥`;
    }
    
    if (recent_frequencies && recent_frequencies.length > 0) {
      const mostUsed = recent_frequencies[0];
      return isGerman
        ? `Du hast zuletzt oft ${mostUsed.name} genutzt. Wie geht es dir damit?`
        : `You've been using ${mostUsed.name} a lot recently. How's that going?`;
    }

    if (journal_entries && journal_entries > 0) {
      return isGerman
        ? `Ich habe deine letzten Tagebucheinträge gesehen. Möchtest du darüber sprechen?`
        : `I've seen your recent journal entries. Would you like to talk about them?`;
    }

    return isGerman
      ? 'Wie kann ich dich heute unterstützen?'
      : 'How can I support you today?';
  };

  if (loading) {
    return null;
  }

  return (
    <div className="daily-checkin-overlay" data-testid="daily-checkin-dialog">
      <div className="daily-checkin-dialog">
        <button 
          className="checkin-close-btn" 
          onClick={handleSkip}
          data-testid="checkin-close"
        >
          <X size={20} />
        </button>

        <div className="checkin-header">
          <div className="checkin-icon">
            <Sparkles size={32} />
          </div>
          <h2 className="checkin-title">
            {getPersonalizedGreeting()}! 👋
          </h2>
          <p className="checkin-subtitle">
            {isGerman 
              ? 'Dein ZENTA-Begleiter möchte wissen, wie es dir geht'
              : 'Your ZENTA companion wants to know how you\'re doing'}
          </p>
        </div>

        {getInsightMessage() && (
          <div className="checkin-insight">
            <MessageCircle size={16} />
            <span>{getInsightMessage()}</span>
          </div>
        )}

        <div className="checkin-mood-section">
          <p className="mood-question">
            {isGerman ? 'Wie fühlst du dich heute?' : 'How are you feeling today?'}
          </p>
          <div className="mood-options">
            {moods.map((mood) => (
              <button
                key={mood.value}
                className={`mood-option ${selectedMood === mood.value ? 'selected' : ''}`}
                onClick={() => handleMoodSelect(mood.value)}
                data-testid={`mood-option-${mood.value}`}
              >
                <span className="mood-emoji">{mood.emoji}</span>
                <span className="mood-label">{mood.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="checkin-actions">
          <button
            className="checkin-btn-primary"
            onClick={handleStartConversation}
            disabled={!selectedMood}
            data-testid="checkin-start-chat"
          >
            <MessageCircle size={18} />
            {isGerman ? 'Mit Begleiter sprechen' : 'Talk to companion'}
          </button>
          <button
            className="checkin-btn-secondary"
            onClick={handleSkip}
            data-testid="checkin-skip"
          >
            {isGerman ? 'Später' : 'Later'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DailyCheckinDialog;
